import React from 'react';
import './Director.css'; // Import the specific CSS

// Placeholder Images (REPLACE and OPTIMIZE THESE FILES)
import director1Placeholder from './Baby.png';
import director2Placeholder from './Rajendra Singh.png';
import Swati from './Swati.jpg';


// Reusable Intersection Observer Hook (Keep as is)
const useVisibility = (options) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const elementRef = React.useRef(null);
    const observerRef = React.useRef(null);

    React.useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Unobserve after first view for performance.
                    // If you want animations to re-trigger on scroll up/down, comment this out.
                    observerRef.current.unobserve(currentElement);
                }
                // No 'else' means it triggers once. Add else with setIsVisible(false) to re-trigger.
            });
        }, options);
        observerRef.current.observe(currentElement);
        return () => { if (observerRef.current && currentElement) observerRef.current.unobserve(currentElement); };
    }, [options]); // Dependency array ensures effect runs if options change (though they don't here)
    return [elementRef, isVisible];
};


// Helper to wrap words in spans for animation (Keep as is)
const WordAnimator = ({ text, baseDelay = 0 }) => {
    const words = text.split(' ');
    return words.map((word, index) => (
        <span key={index} className="director-word-reveal-wrapper">
            <span
                className="director-word-reveal-inner"
                style={{ '--word-delay': `${baseDelay + index * 0.04}s` }}
            >
                {word}
            </span>
        </span>
        // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    )).reduce((acc, elem) => acc === null ? [elem] : [...acc, ' ', elem], null);
};

// Director Logo Component (Keep as is)
const DirectorLogo = () => (
    <a href="/" className="director-logo-link" aria-label="Healthcare Solutions Home">
       {/* Minimalist Logo for Footer Context - Replace if needed */}
       <svg className="director-logo-svg" viewBox="0 0 50 25" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="dirLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                     <stop offset="0%" stopColor="var(--dir-accent-blue)"/>
                     <stop offset="100%" stopColor="var(--dir-accent-green)"/>
                </linearGradient>
            </defs>
            <path d="M5 5 L15 20 L25 5 Z M25 5 L35 20 L45 5 Z" stroke="url(#dirLogoGrad)" strokeWidth="2" fill="none"/>
       </svg>
   </a>
);


function DirectorsSection() {
    const [sectionRef, isSectionVisible] = useVisibility({ threshold: 0.1, triggerOnce: true });
    const [director1Ref, isDirector1Visible] = useVisibility({ threshold: 0.3, triggerOnce: true });
    const [director2Ref, isDirector2Visible] = useVisibility({ threshold: 0.3, triggerOnce: true });
    // Add a ref for the third director
    const [director3Ref, isDirector3Visible] = useVisibility({ threshold: 0.3, triggerOnce: true });


    // Keep the longer speeches (Ensure these aren't excessively long causing perf issues)
    const director1Speech = "In the intricate tapestry of modern healthcare, our primary focus remains steadfast: empowering those at the front lines. We believe that by providing unparalleled support, fostering a culture of continuous learning, and leveraging cutting-edge technology, we unlock the potential for truly transformative patient care. Our commitment is not merely to fill roles, but to build synergistic partnerships that resonate with trust, ethical practice, and a shared dedication to elevating community health outcomes for generations to come.";
    const director2Speech = "Navigating the complexities of the healthcare sector demands foresight, agility, and an unwavering commitment to quality. We champion a proactive approach, anticipating industry shifts and equipping our partners with strategic staffing insights and state-of-the-art resources. Our mission transcends simple placement; we aim to be catalysts for operational excellence, ensuring that every facility we serve is positioned to not only meet but exceed the highest standards of care delivery in an ever-evolving landscape.";
    // Assuming director 3 uses the same speech for now, replace if different
    const director3Speech = "In the intricate tapestry of modern healthcare, our primary focus remains steadfast: empowering those at the front lines. We believe that by providing unparalleled support, fostering a culture of continuous learning, and leveraging cutting-edge technology, we unlock the potential for truly transformative patient care. Our commitment is not merely to fill roles, but to build synergistic partnerships that resonate with trust, ethical practice, and a shared dedication to elevating community health outcomes for generations to come.";


    return (
        <section ref={sectionRef} className={`directors-section-wrapper ${isSectionVisible ? 'is-visible' : ''}`}>
            {/* Light Background Elements */}
            <div className="directors-background-texture"></div>
            <div className="directors-background-blob blob-top-left"></div>
            <div className="directors-background-blob blob-bottom-right"></div>

            <div className="directors-container">
                {/* Refined Section Title */}
                <div className="directors-title-container">
                    <h2 className="directors-section-title">Leadership Perspectives</h2>
                    <p className="directors-section-subtitle">Guiding Principles from Our Directors</p>
                </div>

                {/* === Director 1 Block (Image Left) === */}
                <div ref={director1Ref} className={`director-block director-layout-a ${isDirector1Visible ? 'is-visible' : ''}`}>
                    <div className="director-image-container">
                    <div className="director-content-container">
                        <div className="director-content-inner">
                            {/* Elegant Quote Mark */}
                            <svg className="director-quote-graphic" width="80" height="64" viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M28.88 64C21.52 64 15.2133 61.3867 9.96 56.16C4.70667 50.9333 2.08 44.7467 2.08 37.6C2.08 30.3467 4.02667 23.68 7.92 17.6C11.8133 11.52 17.76 6.61333 25.76 2.88L30.56 8C24.8533 11.0933 20.48 15.1467 17.44 20.16C14.4 25.0667 12.88 30.6667 12.88 36.96C16.88 34.7733 21.4933 33.68 26.72 33.68C33 33.68 38.2133 35.8667 42.36 40.24C46.6133 44.5067 48.74 49.6533 48.72 55.68C48.6667 60.6933 46.6133 64.4267 42.56 66.88C38.5067 69.3333 33.9467 70.4 28.88 70L28.88 64Z" fill="url(#quoteGradient)"/>
                                <path d="M68.88 64C61.52 64 55.2133 61.3867 49.96 56.16C44.7067 50.9333 42.08 44.7467 42.08 37.6C42.08 30.3467 44.0267 23.68 47.92 17.6C51.8133 11.52 57.76 6.61333 65.76 2.88L70.56 8C64.8533 11.0933 60.48 15.1467 57.44 20.16C54.4 25.0667 52.88 30.6667 52.88 36.96C56.88 34.7733 61.4933 33.68 66.72 33.68C73 33.68 78.2133 35.8667 82.36 40.24C86.6133 44.5067 88.74 49.6533 88.72 55.68C88.6667 60.6933 86.6133 64.4267 82.56 66.88C78.5067 69.3333 73.9467 70.4 68.88 70L68.88 64Z" fill="url(#quoteGradient)"/>
                                <defs>
                                    <linearGradient id="quoteGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="var(--dir-sky-blue)" stopOpacity="0.15"/>
                                        <stop offset="100%" stopColor="var(--dir-soft-green)" stopOpacity="0.15"/>
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div className="director-speech-wrapper">
                                <p className="director-speech">
                                    <WordAnimator text={director1Speech} baseDelay={0.5} />
                                </p>
                            </div>
                            <div className="director-signature-block">
                                <p className="director-title">Director Voice</p>
                                {/* <DirectorLogo /> */} {/* Optional logo here */}
                            </div>
                        </div>
                    </div>
                         {/* Animated Outline Effect */}
                        <div className="director-image-outline"></div>
                        <div className="director-image-corner-accent accent-top-left"></div>
                        <div className="director-image-corner-accent accent-bottom-right"></div>
                    </div>
                    <div className="director-image-container">
                     <div className="director-content-container">
                        <div className="director-content-inner">
                           <svg className="director-quote-graphic" width="80" height="64" viewBox="0 0 80 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                {/* Use same SVG paths and gradient def */}
                                <use xlinkHref="#quoteGradientPath1" fill="url(#quoteGradient)"/>
                                <use xlinkHref="#quoteGradientPath2" fill="url(#quoteGradient)"/>
                                {/* Defs are already defined above, but including again for clarity if needed */}
                                <defs>
                                    <path id="quoteGradientPath1" d="M28.88 64C21.52 64 15.2133 61.3867 9.96 56.16C4.70667 50.9333 2.08 44.7467 2.08 37.6C2.08 30.3467 4.02667 23.68 7.92 17.6C11.8133 11.52 17.76 6.61333 25.76 2.88L30.56 8C24.8533 11.0933 20.48 15.1467 17.44 20.16C14.4 25.0667 12.88 30.6667 12.88 36.96C16.88 34.7733 21.4933 33.68 26.72 33.68C33 33.68 38.2133 35.8667 42.36 40.24C46.6133 44.5067 48.74 49.6533 48.72 55.68C48.6667 60.6933 46.6133 64.4267 42.56 66.88C38.5067 69.3333 33.9467 70.4 28.88 70L28.88 64Z" />
                                    <path id="quoteGradientPath2" d="M68.88 64C61.52 64 55.2133 61.3867 49.96 56.16C44.7067 50.9333 42.08 44.7467 42.08 37.6C42.08 30.3467 44.0267 23.68 47.92 17.6C51.8133 11.52 57.76 6.61333 65.76 2.88L70.56 8C64.8533 11.0933 60.48 15.1467 57.44 20.16C54.4 25.0667 52.88 30.6667 52.88 36.96C56.88 34.7733 61.4933 33.68 66.72 33.68C73 33.68 78.2133 35.8667 82.36 40.24C86.6133 44.5067 88.74 49.6533 88.72 55.68C88.6667 60.6933 86.6133 64.4267 82.56 66.88C78.5067 69.3333 73.9467 70.4 68.88 70L68.88 64Z" />
                                </defs>
                           </svg>
                             <div className="director-speech-wrapper">
                                <p className="director-speech">
                                     <WordAnimator text={director2Speech} baseDelay={0.5} />
                                </p>
                             </div>
                            <div className="director-signature-block">
                                <p className="director-title">Director Voice</p>
                                {/* <DirectorLogo /> */}
                            </div>
                        </div>
                    </div>
                         <div className="director-image-outline"></div>
                         <div className="director-image-corner-accent accent-top-right"></div>
                         <div className="director-image-corner-accent accent-bottom-left"></div>
                     </div>
                </div>

                 

              </div>
        </section>
    );
}

export default DirectorsSection;